import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { avatarUrl } from "@/lib/api";
import { RoleBadge } from "@/components/shared";
import {
  LayoutDashboard, Briefcase, Inbox, FileText, BookOpen, Users, Archive,
  LogOut, Menu, X, Map, MessageSquare, Share2, HelpCircle, Building2, Globe, UserCircle, GraduationCap, DollarSign, ListChecks, Wallet, FileBarChart,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import NotificationBell from "@/components/NotificationBell";
import { LanguageToggle } from "@/components/LanguageToggle";
import { HowItWorksModal } from "@/components/HowItWorks";
import { WEBSITE_URL, shareApp } from "@/lib/constants";

const NAV = {
  admin: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/jobs", label: "Jobs", icon: Briefcase },
    { to: "/map", label: "Map", icon: Map },
    { to: "/requests", label: "Requests", icon: Inbox },
    { to: "/messages", label: "Messages", icon: MessageSquare },
    { to: "/bids", label: "Bids", icon: FileText },
    { to: "/pricebook", label: "Price Book", icon: BookOpen },
    { to: "/crew", label: "Crew", icon: Users },
    { to: "/clients", label: "Clients", icon: Building2 },
    { to: "/pricing", label: "Pricing", icon: DollarSign },
    { to: "/reports", label: "Reports", icon: FileBarChart },
    { to: "/checklists", label: "Checklists", icon: ListChecks },
    { to: "/training", label: "Training", icon: GraduationCap },
    { to: "/archived", label: "Archived", icon: Archive },
    { to: "/guide", label: "Admin Guide", icon: HelpCircle },
  ],
  crew: [
    { to: "/contractor", label: "My Profile", icon: Map },
    { to: "/jobs", label: "My Jobs", icon: Briefcase },
    { to: "/pay-rates", label: "My Pay Rates", icon: Wallet },
    { to: "/training", label: "Training", icon: GraduationCap },
    { to: "/messages", label: "Messages", icon: MessageSquare },
    { to: "/profile", label: "Account Settings", icon: UserCircle },
  ],
  client: [
    { to: "/requests", label: "My Requests", icon: Inbox },
    { to: "/jobs", label: "My Jobs", icon: Briefcase },
    { to: "/pricebook", label: "Prices", icon: BookOpen },
    { to: "/messages", label: "Messages", icon: MessageSquare },
    { to: "/profile", label: "My Profile", icon: UserCircle },
  ],
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [howOpen, setHowOpen] = useState(false);
  const navigate = useNavigate();
  const items = NAV[user?.role] || [];

  useEffect(() => {
    if (user?.role === "client" && !localStorage.getItem("ss_seen_tour")) {
      setHowOpen(true);
      localStorage.setItem("ss_seen_tour", "1");
    }
  }, [user?.role]);

  const NavItems = ({ onClick }) => (
    <nav className="flex flex-col gap-1">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <NavLink
            key={it.to}
            to={it.to}
            onClick={onClick}
            data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 font-head font-bold uppercase text-sm tracking-wide border-2 transition-all ${
                isActive
                  ? "bg-[#FF5722] text-black border-black shadow-[3px_3px_0px_#000]"
                  : "text-zinc-400 border-transparent hover:text-white hover:border-zinc-700"
              }`
            }
          >
            <Icon size={18} /> {t(`nav.${it.label}`, it.label)}
          </NavLink>
        );
      })}
      {user?.role === "client" && (
        <button
          type="button"
          data-testid="nav-how-it-works"
          onClick={() => { onClick?.(); setHowOpen(true); }}
          className="flex items-center gap-3 px-3 py-2.5 font-head font-bold uppercase text-sm tracking-wide border-2 border-transparent text-zinc-400 hover:text-white hover:border-zinc-700 transition-all text-left"
        >
          <HelpCircle size={18} /> {t("login.howItWorks")}
        </button>
      )}
    </nav>
  );

  return (
    <div className="min-h-screen flex bg-[#09090b]">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-[#141417] border-r-2 border-zinc-800 p-4 shrink-0 sticky top-0 h-screen">
        <div className="flex items-center justify-between">
          <Brand />
          <NotificationBell />
        </div>
        <div className="mt-6 flex-1 overflow-y-auto min-h-0"><NavItems /></div>
        <div className="pt-3"><LanguageToggle /></div>
        <UserBox user={user} logout={logout} />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-[#141417] border-r-[3px] border-[#FF5722] p-4 flex flex-col animate-rise">
            <div className="flex items-center justify-between">
              <Brand />
              <button data-testid="close-drawer-btn" onClick={() => setOpen(false)} className="text-zinc-400"><X /></button>
            </div>
            <div className="mt-6 flex-1 overflow-y-auto min-h-0"><NavItems onClick={() => setOpen(false)} /></div>
            <div className="pt-3"><LanguageToggle /></div>
            <UserBox user={user} logout={logout} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-40 bg-[#09090b]/95 backdrop-blur-md border-b-2 border-zinc-800 px-4 sm:px-6 py-3 flex items-center justify-between lg:hidden">
          <button data-testid="open-drawer-btn" onClick={() => setOpen(true)} className="text-white"><Menu /></button>
          <Brand small />
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <NotificationBell />
            <button data-testid="mobile-logout-btn" onClick={logout} className="text-zinc-400"><LogOut size={20} /></button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-7xl w-full mx-auto">{children}</main>
        <footer className="border-t-2 border-zinc-800 px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-center">
          <div className="font-mono text-[11px] text-zinc-600">© {new Date().getFullYear()} Swift Signings &amp; Services LLC</div>
          <div className="flex items-center gap-4">
            <button data-testid="footer-share-btn" onClick={shareApp} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-[#FF5722] font-head font-bold uppercase text-xs tracking-wide transition-colors"><Share2 size={14} /> Share App</button>
            <a data-testid="footer-website-link" href={WEBSITE_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-[#FF5722] font-head font-bold uppercase text-xs tracking-wide transition-colors"><Globe size={14} /> swiftsigningsandservices.com</a>
          </div>
        </footer>
      </div>

      <HowItWorksModal open={howOpen} onClose={() => setHowOpen(false)} />
    </div>
  );
}

function Brand({ small }) {
  return (
    <div className="flex items-center gap-2">
      <img src="/sss-logo.png" alt="Swift Signings & Services LLC" className="border-2 border-black shadow-[2px_2px_0px_#000] object-cover shrink-0" style={{ width: small ? 32 : 38, height: small ? 32 : 38 }} />
      <div className="leading-none">
        <div className="font-head font-black text-white uppercase tracking-tight text-sm">Swift</div>
        <div className="font-mono text-[9px] text-[#FF5722] uppercase tracking-widest">Signings & Services</div>
      </div>
    </div>
  );
}

function UserBox({ user, logout }) {
  return (
    <div className="mt-4 pt-4 border-t-2 border-zinc-800">
      <div className="flex items-center gap-2 mb-2">
        {avatarUrl(user) ? (
          <img src={avatarUrl(user)} alt="" className="w-8 h-8 border-2 border-zinc-700 object-cover" />
        ) : (
          <div className="w-8 h-8 bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center font-head font-black text-[#FF5722]">
            {user?.name?.[0]?.toUpperCase() || "?"}
          </div>
        )}
        <div className="min-w-0">
          <div className="text-xs text-white font-semibold truncate">{user?.name}</div>
          <RoleBadge role={user?.role} />
        </div>
      </div>
      <button
        data-testid="share-app-btn"
        onClick={async () => { const url = window.location.origin; try { if (navigator.share) { await navigator.share({ title: "Swift Signings & Services", url }); } else { await navigator.clipboard.writeText(url); } } catch (e) { console.debug("Share dismissed:", e); } }}
        className="hidden lg:flex items-center gap-2 w-full text-zinc-400 hover:text-[#FF5722] text-sm font-head font-bold uppercase tracking-wide px-2 py-2 transition-colors"
      >
        <Share2 size={16} /> Share app
      </button>
      <button
        data-testid="logout-btn"
        onClick={logout}
        className="hidden lg:flex items-center gap-2 w-full text-zinc-400 hover:text-red-400 text-sm font-head font-bold uppercase tracking-wide px-2 py-2 transition-colors"
      >
        <LogOut size={16} /> Log out
      </button>
    </div>
  );
}
